import { useEffect, useState } from "react";
import { Users, Globe, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { TabKicker, TabSectionTitle } from "./_shell";

/**
 * CountrySocieties — a tile grid on the International tab showing
 * likely national/faith societies inferred from the uni's top source
 * countries + a curated list of the biggest known societies.
 *
 * Data comes from `/api/universities/{id}/international-signals` —
 * we already fetch top_countries there. For each country we render a
 * flag emoji + expected society name + a student-search-url link
 * to help applicants find the actual society page.
 */

// Country → best-known society label + emoji flag anchor. Not
// exhaustive; we render top-8 countries per uni.
const COUNTRY_META = {
  "China":       { flag: "🇨🇳", society: "Chinese Students' & Scholars' Association" },
  "India":       { flag: "🇮🇳", society: "Indian Society" },
  "USA":         { flag: "🇺🇸", society: "North American Society" },
  "United States":{flag: "🇺🇸", society: "North American Society" },
  "Nigeria":     { flag: "🇳🇬", society: "Nigerian Society" },
  "Pakistan":    { flag: "🇵🇰", society: "Pakistan Society" },
  "Germany":     { flag: "🇩🇪", society: "German Society" },
  "France":      { flag: "🇫🇷", society: "French Society" },
  "Hong Kong":   { flag: "🇭🇰", society: "Hong Kong Public Affairs / HKSA" },
  "Singapore":   { flag: "🇸🇬", society: "Singapore Society" },
  "Malaysia":    { flag: "🇲🇾", society: "Malaysian Students' Society" },
  "Italy":       { flag: "🇮🇹", society: "Italian Society" },
  "Spain":       { flag: "🇪🇸", society: "Spanish Society" },
  "Greece":      { flag: "🇬🇷", society: "Hellenic Society" },
  "Canada":      { flag: "🇨🇦", society: "Canadian Society" },
  "Australia":   { flag: "🇦🇺", society: "Australian & New Zealand Society" },
  "Kenya":       { flag: "🇰🇪", society: "Kenyan Society" },
  "Ghana":       { flag: "🇬🇭", society: "Ghanaian Society" },
  "South Africa":{ flag: "🇿🇦", society: "South African Society" },
  "Egypt":       { flag: "🇪🇬", society: "Egyptian Society" },
  "Saudi Arabia":{ flag: "🇸🇦", society: "Saudi Students' Society" },
  "UAE":         { flag: "🇦🇪", society: "Emirati Students' Society" },
  "Turkey":      { flag: "🇹🇷", society: "Turkish Society" },
  "Poland":      { flag: "🇵🇱", society: "Polish Society" },
  "Romania":     { flag: "🇷🇴", society: "Romanian Society" },
  "Bangladesh":  { flag: "🇧🇩", society: "Bangladeshi Society" },
  "Sri Lanka":   { flag: "🇱🇰", society: "Sri Lankan Society" },
  "Vietnam":     { flag: "🇻🇳", society: "Vietnamese Society" },
  "Thailand":    { flag: "🇹🇭", society: "Thai Society" },
  "South Korea": { flag: "🇰🇷", society: "Korean Society" },
  "Japan":       { flag: "🇯🇵", society: "Japanese Society" },
  "Brazil":      { flag: "🇧🇷", society: "Brazilian Society" },
  "Mexico":      { flag: "🇲🇽", society: "Mexican Society" },
};

export default function CountrySocieties({ uniId, uniName }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uniId}/international-signals`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uniId]);

  const countries = (data?.international?.top_countries || []).slice(0, 8);
  if (countries.length === 0) return null;

  return (
    <section className="mb-8" data-testid="country-societies">
      <div>
        <TabKicker>03 · National Societies</TabKicker>
        <TabSectionTitle>Your community on campus</TabSectionTitle>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {countries.map((c) => {
          const meta = COUNTRY_META[c.country] || { flag: "🌐", society: `${c.country} Society` };
          const searchQ = encodeURIComponent(`${uniName} ${c.country} society students union`);
          return (
            <a
              key={c.country}
              href={`https://duckduckgo.com/?q=${searchQ}`}
              target="_blank" rel="noopener noreferrer"
              className="p-4 border border-[#002147]/10 bg-white/40 hover:bg-white/80 hover:border-[#7C3AED] transition-colors group"
              data-testid={`society-${c.country.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl leading-none">{meta.flag}</span>
                <span className="font-mono text-[9px] uppercase tracking-widest text-[#002147]/50">
                  {c.country}
                </span>
              </div>
              <div className="font-serif text-sm text-[#002147] leading-tight group-hover:text-[#7C3AED] transition-colors">
                {meta.society}
              </div>
              <div className="mt-2 flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-[#002147]/50">
                <span>{c.students?.toLocaleString() || "—"} students</span>
                <ExternalLink className="h-2.5 w-2.5 text-[#002147]/30 group-hover:text-[#7C3AED]" />
              </div>
            </a>
          );
        })}
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#002147]/50">
        <Users className="inline h-3 w-3 mr-1" />
        Inferred from source-country data · click any tile to find the official student-union page
      </p>
    </section>
  );
}
